import { cx } from '@emotion/css';
import { createErrorReport } from 'api-client/admin';
import type { IconName } from 'bc-ods';
import { ODSAlert, ODSButton, ODSEmptyState, ODSGroup } from 'bc-ods';
import ErrorReportForm from 'components/ErrorReportForm';
import ErrorSVG from 'error.svg?react';
import AccessRequestSVG from 'accessRequest.svg?react';
import React, { type FC, useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import RequestAccessForm from 'components/RequestAccessForm';
import { useNavigate } from 'react-router-dom';

interface Props {
  errorCode?: number | string;
  message?: string;
  clearState?: () => void;
  stackTrace?: string;
  emptystate?: boolean;
  icon?: IconName;
  showBugReport?: boolean;
  showReload?: boolean;
  showReport?: boolean;
  cta?: React.ReactNode;
  canRequestAccess?: boolean;
}

const messages = defineMessages({
  backLink: {
    id: 'ErrorMessage.backLink',
    defaultMessage: 'Back',
  },
  error: {
    id: 'ErrorMessage.main',
    defaultMessage:
      '{errorCode, select, ' +
      '  403 {Access denied} ' +
      '  404 {Not found} ' +
      '  429 {Too many requests} ' +
      '  removed {Not found} ' +
      '  offline {Offline}' +
      '  other {An unexpected error occurred}}',
  },
  errorSubtext: {
    id: 'ErrorMessage.subtext',
    defaultMessage:
      '{errorCode, select, ' +
      "  403 {You don't have permission to access this page.{br}Please reach out to the plan owner to request access.} " +
      '  404 {Please double check that the URL is entered correctly and that you have permission to access this page.} ' +
      "  429 {You've submitted too many requests for the server to process. Please wait a moment and try again.} " +
      '  removed {The content may have been deleted or removed. Please double check the link and try again.} ' +
      '  offline {Your device appears to be offline.{br}Please check your internet connection and try again.}' +
      "  other {Something went wrong and the page didn't load properly.{br}Please go back or contact support if the issue persists.}}",
  },
  homeLink: {
    id: 'ErrorMessage.homeLink',
    defaultMessage: '{badRoute, select, true {Go back} other {Reload}}',
  },
  reportError: {
    id: 'ErrorMessage.reportError',
    defaultMessage: 'Add more information',
  },
  reportFailed: {
    id: 'ErrorMessage.reportFailed',
    defaultMessage:
      'We were unable to record the error. If the problem persists, please contact us at support@onebrief.com',
  },
  reportSent: {
    id: 'ErrorMessage.reportSent',
    defaultMessage: 'The error has been recorded and will be reviewed by our team.',
  },
  updateFailed: {
    id: 'ErrorMessage.updateFailed',
    defaultMessage:
      'A network problem prevented your comments from being added to the error report. Wait a few minutes before trying again, or email your comments to support@onebrief.com',
  },
  errorMessage: {
    id: 'ErrorMessage.errorMessage',
    defaultMessage:
      '{errorCode, select, ' +
      '  429 {Error message: Request failed with status code 429 Too Many Requests}' +
      '  other {Error message: {message}}}',
  },
});

const ErrorMessage: FC<Props> = ({
  clearState,
  errorCode: rawErrorCode = -1,
  message = '',
  stackTrace = '',
  emptystate,
  icon = 'triangle-exclamation',
  showBugReport = true,
  showReload = true,
  showReport = true,
  cta: providedCta,
  canRequestAccess = false,
}) => {
  const navigate = useNavigate();
  const [reportId, setReportId] = useState<number>(-1);
  const [reportFailed, setReportFailed] = useState(false);
  const [isUpdatingReport, setUpdatingReport] = useState(false);
  const [updateFailed, setUpdateFailed] = useState(false);

  useEffect(() => {
    window.errorPageLoaded = true;
    void createErrorReport({
      errorMessage: message,
      reportedDescription: '',
      reportedOnUrl: window.location.href,
      stackTrace,
    })
      .then(({ data }) => {
        setReportId(data?.id ?? -1);
      })
      .catch(() => {
        setReportFailed(true);
      });
    return () => {
      delete window.errorPageLoaded;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorCode = navigator.onLine ? rawErrorCode : 'offline';
  const badRoute = errorCode.toString() === '404' || errorCode.toString() === '403';

  const goBack = useCallback(() => {
    if (badRoute) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/');
      }
    } else {
      navigate(0);
    }
    if (clearState) {
      clearState();
    }
  }, [badRoute, clearState, navigate]);

  const navToHomeWithSearch = useCallback(
    (searchParams) => {
      navigate(`/${searchParams}`);
    },
    [navigate]
  );

  const title = <FormattedMessage {...messages.error} values={{ errorCode }} />;

  const description = (
    <>
      <p className="mb-2">
        <FormattedMessage {...messages.errorSubtext} values={{ errorCode, br: <br /> }} />
      </p>
      {(message || errorCode === 429) && (
        <div className="text-secondary small fst-italic mb-2">
          {<FormattedMessage {...messages.errorMessage} values={{ message, errorCode }} />}
        </div>
      )}
      {showReport &&
        (reportFailed ? (
          <ODSAlert color="warning">
            <FormattedMessage {...messages.reportFailed} />
          </ODSAlert>
        ) : (
          <p>
            <FormattedMessage {...messages.reportSent} />
          </p>
        ))}
    </>
  );

  const cta =
    providedCta ??
    (showBugReport || showReload ? (
      <ODSGroup>
        {showBugReport && (
          <ODSButton
            icon="bug"
            outline
            disabled={reportId === -1 || reportFailed}
            onClick={() => {
              setUpdateFailed(false);
              setUpdatingReport(true);
            }}
          >
            <FormattedMessage {...messages.reportError} />
          </ODSButton>
        )}
        {showReload && (
          <ODSButton
            cta
            testSelector="reload-app"
            onClick={() => {
              goBack();
            }}
          >
            <FormattedMessage {...messages.homeLink} values={{ badRoute }} />
          </ODSButton>
        )}
      </ODSGroup>
    ) : null);

  if (badRoute && canRequestAccess) {
    return (
      <div
        className={cx(
          'd-flex flex-grow-1 w-100 h-100 gap-2 mx-auto flex-column justify-content-center align-items-center',
          {
            'p-3 overflow-y-auto': !emptystate,
          }
        )}
        css={{ maxWidth: '55rem' }}
        data-testid="error-page"
      >
        <div className="d-flex flex-grow-1 align-items-center justify-content-center">
          <AccessRequestSVG className="flex-shrink-0 me-5" css={{ width: 'min(30vw, 20rem)' }} />
          <div className="d-flex flex-column gap-3 align-items-self-start justify-content-center">
            <RequestAccessForm
              onComplete={() => {
                navToHomeWithSearch('?openNotifications=true');
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={cx('d-flex flex-grow-1 h-100 w-100 gap-2 mx-auto flex-column', {
          'p-3 overflow-y-auto': !emptystate,
        })}
        css={{ maxWidth: '55rem' }}
        data-testid="error-page"
      >
        {updateFailed && (
          <ODSAlert color="warning">
            <FormattedMessage {...messages.updateFailed} />
          </ODSAlert>
        )}
        <div className="d-flex flex-grow-1 align-items-center justify-content-center">
          {emptystate ? (
            <ODSEmptyState
              title={title}
              description={description}
              icon={icon}
              cta={cta}
              size="sm"
              className="py-3"
            />
          ) : (
            <>
              <ErrorSVG className="flex-shrink-0 me-5" css={{ width: 'min(30vw, 20rem)' }} />
              <div className="d-flex flex-column gap-3 align-items-self-start justify-content-center">
                <h1 className="mb-0">{title}</h1>
                {description}
                {cta}
              </div>
            </>
          )}
        </div>
      </div>
      <ErrorReportForm
        message={message}
        stackTrace={stackTrace}
        reportId={reportId}
        isOpen={isUpdatingReport}
        onComplete={(suceeded) => {
          if (suceeded) {
            goBack();
          } else {
            setUpdatingReport(false);
            setUpdateFailed(true);
          }
        }}
        toggle={() => {
          setUpdatingReport(false);
        }}
      />
    </>
  );
};

export default ErrorMessage;
